/**
 * ScrollLock Component
 */

import React, { useMemo, useState, FC } from 'react';
import { useLayoutEffect } from '../hooks/useIsomorphicLayoutEffect';
import { ScrollLockProps, GlobalStylesProps } from './scrollLock.types';
import { GlobalStyles } from './scrollLock.style';
import generateId from '../../utils/generateId';

export const CLASS_NAME_PREFIX = 'mlb-scroll-lock-';

const ScrollLock: FC<ScrollLockProps> = ({
  element = document?.body,
}: ScrollLockProps) => {

  const className = useMemo(() => generateId(CLASS_NAME_PREFIX), [element]);
  const [styles, setStyles] = useState<GlobalStylesProps>();

  useLayoutEffect(() => {

    if (!element) {
      return;
    }

    // Feature detect overscroll-behavior css
    const isOverscrollBehaviorSupported = typeof CSS !== 'undefined' && CSS.supports('overscroll-behavior', 'contain');

    // Detect if element is document body
    const elementIsDocumentBody = element === document.body;

    // Retain original values
    const offsetX = elementIsDocumentBody ? window.pageXOffset : element.scrollLeft;
    const offsetY = elementIsDocumentBody ? window.pageYOffset : element.scrollTop;

    setStyles({
      class: className,
      isOverscrollBehaviorSupported,
      left: offsetX,
      top: offsetY,
      elementIsDocumentBody,
    });

    element.classList.add(className);

    return () => {

      element.classList.remove(className);

      if(!isOverscrollBehaviorSupported) {

        // Restore scroll
        if(elementIsDocumentBody) {
          window.scrollTo(offsetX, offsetY);
        } else {
          element.scrollLeft = offsetX;
          element.scrollTop = offsetY;
        }
      }

    };

  }, [element, className]);

  if(!element || !styles) {

    return null;

  }

  return <GlobalStyles {...styles} />;

};

export default ScrollLock;
